import { InfoIcon } from '@phosphor-icons/react';
import { Drawer as AntdDrawer, Skeleton } from 'antd';

import { CloseButton } from '@/components/common/close-button';
import { Switch } from '@/components/common/switch';

import { useNotificationSettings, useUpdateNotificationSettings } from '../hooks';
import type { NotificationSettings } from '../types';

import styles from './notification-settings-drawer.module.scss';

type ToggleRow = {
  key: keyof NotificationSettings;
  title: string;
  description: string;
};

/** The four delivery channels, in Figma order (node 2389-740152). */
const ROWS: ToggleRow[] = [
  {
    key: 'general',
    title: 'General',
    description: 'Warranty & Support ticket notification settings',
  },
  {
    key: 'subscription',
    title: 'Subscription',
    description: 'Billing and subscription status updates',
  },
  {
    key: 'scheduleCommands',
    title: 'Schedule and Commands',
    description: 'Scheduled device operations (start/stop) notifications',
  },
  {
    key: 'device',
    title: 'Device notifications',
    description: 'Critical alerts (pump overflow, low fuel, device offline) are always delivered',
  },
];

export type NotificationSettingsDrawerProps = {
  open: boolean;
  onClose: () => void;
};

/**
 * Notification Settings drawer (Figma 2389-740152). Reads the grower's delivery
 * preferences and flips one channel per toggle — optimistically, so each switch
 * responds instantly while the change persists in the background (story 101880).
 */
export function NotificationSettingsDrawer({ open, onClose }: NotificationSettingsDrawerProps) {
  const { data: settings, isLoading } = useNotificationSettings();
  const update = useUpdateNotificationSettings();

  return (
    <AntdDrawer
      open={open}
      onClose={onClose}
      placement="right"
      width={421}
      closable={false}
      title={null}
      footer={null}
      destroyOnHidden
      classNames={{ body: styles.body }}
    >
      <header className={styles.header}>
        <h2 className={styles.title}>Notification Settings</h2>
        <CloseButton aria-label="Close notification settings" onClick={onClose} />
      </header>

      <div className={styles.rows}>
        {isLoading || !settings
          ? ROWS.map((row) => (
              <div key={row.key} className={styles.row}>
                <div className={styles.rowText}>
                  <Skeleton.Input active size="small" style={{ width: 140, height: 18 }} />
                  <Skeleton.Input active size="small" style={{ width: 220, height: 14 }} />
                </div>
                <Switch size="lg" disabled />
              </div>
            ))
          : ROWS.map((row) => (
              <div key={row.key} className={styles.row}>
                <div className={styles.rowText}>
                  <span className={styles.rowTitle}>{row.title}</span>
                  <span className={styles.rowDescription}>{row.description}</span>
                </div>
                <Switch
                  size="lg"
                  checked={settings[row.key]}
                  loading={update.isPending && update.variables?.[row.key] !== undefined}
                  onChange={(checked) => update.mutate({ [row.key]: checked })}
                  aria-label={row.title}
                />
              </div>
            ))}
      </div>

      <footer className={styles.footer}>
        <InfoIcon size={16} weight="regular" />
        <span>Safety-critical alerts are always delivered</span>
      </footer>
    </AntdDrawer>
  );
}
